/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { AppInsights } from 'applicationinsights-js';
import { ITelemetryContext, ITraceLogger, ITraceLoggerParams, LogLevel } from '../interfaces';
import { IDictionary } from '../interfaces/IDictionary';
import { ITrackDependency } from '../interfaces/ITrackDependency';
import { TelemetryBase } from '../telemetry';
import { buildPropertiesList, getPropertiesFromContext, messageTemplateKey, shouldSkipLogging } from './app-insights-helper';
import { safeStringify } from './logging-helper';

/**
 * Client-side logger that sends messages to Application Insights
 */
export class AppInsightsJSLogger implements ITraceLogger {
    /**
     * Mapping between telemetry framework LogLevel and AppInsights JS Severity Levels
     */
    private static _severityLevelMapping: Map<LogLevel, number> = new Map([
        [LogLevel.Trace, 0],
        [LogLevel.Information, 1],
        [LogLevel.Warning, 2],
        [LogLevel.Error, 3],
        [LogLevel.Critical, 4]
    ]);

    private options: ITraceLoggerParams = <ITraceLoggerParams>{};

    /**
     * Creates a new instance of AppInsights Logger
     * @param instrumentationKey The instrumentation key for the Application Insights instance
     */
    constructor() {
        AppInsights.trackEvent('Client version of Application Insights registered');
    }

    public initialize(options: ITraceLoggerParams): void {
        this.options = options;
    }

    /**
     * @internal
     * Logs a trace message to AppInsights
     * @param logLevel LogLevel of the message
     * @param context Telemetry context to add to log
     * @param messageTemplate Structured log formatted string
     * @param placeholders List of placeholders in messageTemplate along with position in string
     * @param values Arguments to replace placeholders
     * @param customTags Array of strings to attach to log
     */
    public sendLog(
        logLevel: LogLevel,
        context: ITelemetryContext,
        messageTemplate: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        placeholders: Map<string, any>,
        customTags?: string[],
        exception?: Error
    ): void {
        // Convert ITelemetry LogLevel to corresponding AppInsights SeverityLevel when logging
        const severityLevel = AppInsightsJSLogger._severityLevelMapping.get(logLevel);

        if (shouldSkipLogging(severityLevel, this.options.isDebug)) {
            return;
        }

        // Build properties list with telemetry context and message template arguments
        const properties: IDictionary<string> = buildPropertiesList(context, placeholders, customTags);

        if (exception === undefined) {
            // Send log to AppInsights as trace event
            AppInsights.trackTrace(messageTemplate, properties, severityLevel);
        } else {
            // Attach message template as custom property
            properties[messageTemplateKey] = messageTemplate;

            // Send log to AppInsights as exception event
            AppInsights.trackException(exception, undefined, properties, undefined, severityLevel);
        }
    }

    /**
     * @internal
     * Logs a trace message to AppInsights
     * @param logLevel Severity level of the log
     * @param message String to log
     * @param args Any additional objects to log
     * @param context Additional properties to add to the log
     */
    public log(logLevel: LogLevel, message: string, args: unknown[], context: ITelemetryContext): void {
        // Convert ITelemetry LogLevel to corresponding AppInsights SeverityLevel when logging
        const severityLevel = AppInsightsJSLogger._severityLevelMapping.get(logLevel);

        if (shouldSkipLogging(severityLevel, this.options.isDebug)) {
            return;
        }

        // Append any extra arguments to the message
        let fullMessage = message;
        args.forEach(arg => {
            fullMessage = fullMessage.concat('\n', safeStringify(arg));
        });
        AppInsights.trackTrace(`${logLevel}: ${fullMessage}`, getPropertiesFromContext(context));
    }
    /**
     * @internal
     * Logs an error object to AppInsights as an exception
     * @param error Error object to log
     * @param context Additional properties to add to the log
     */
    public exception(error: Error, context: ITelemetryContext): void {
        AppInsights.trackException(error, undefined, getPropertiesFromContext(context));
    }
    /**
     * @internal
     * No-op - not tracking client side metrics
     * @param name name of the metric
     * @param value value of the metric
     */
    public trackMetric(name: string, value: number, context: ITelemetryContext): void {
        // noop
    }
    /**
     * Track a structured event with app insights
     *
     * @template TProperties interface for properties
     * @template TMeasurements interface for measurements to go with the event
     * @param {ITrackEventOptions<TProperties, TMeasurements>} trackEventOptions event options
     */
    public trackEvent<TProperties, TMeasurements>(
        name: string,
        properties?: TProperties,
        measurements?: TMeasurements,
        context?: ITelemetryContext
    ): void {
        AppInsights.trackEvent(name, <IDictionary<string>>(<unknown>properties), <IDictionary<number>>(<unknown>measurements));
    }

    /**
     * Track a page view
     *
     * @param {string} name
     * @param {string} [url]
     */
    public trackPageView(name: string, url?: string): void {
        AppInsights.trackPageView(name, url);
    }

    /**
     * Track request
     * @param options request options
     */
    public trackDependency(options: ITrackDependency): void {
        const id = TelemetryBase.generateGuid();

        // on the client it's a dependency call
        AppInsights.trackDependency(
            id,
            options.method,
            `https://${options.target}${options.name}` || '',
            options.name || '',
            options.duration,
            options.success,
            options.resultCode,
            options.properties
        );
    }
}
